import { useCallback } from 'react';

import { useCompanyPreferences } from '@/api/performance-company-preferences';

/**
 * What the portal knows of one company decision: whether an employee sees, in their own portal, the
 * competency levels a manager evaluation assessed them at.
 *
 * Three answers, not two. Read as "no", a failure would hide the sections of a company that does
 * show them; read as "still on its way", it would spin for ever. So the portal keeps them apart and
 * says which one it is.
 *
 * The single reading of that setting in the portal: the summary tab's two competency sections hang
 * on it, and so does whether the tab is offered at all.
 */
export interface CompetencyVisibility {
    /** The company's answer, or `undefined` while it is not known. */
    areCompetenciesShown: boolean | undefined;
    /** The company's answer could not be read at all. */
    isUnreadable: boolean;
    /** Asks the server for that answer again. */
    retry: () => void;
}

export const useCompetencyVisibility = (): CompetencyVisibility => {
    const { data, isSuccess, isError, refetch } = useCompanyPreferences();
    const retry = useCallback(() => void refetch(), [refetch]);

    return {
        areCompetenciesShown: isSuccess ? data.showCompetencyScoresToTheEmployee : undefined,
        isUnreadable: isError,
        retry,
    };
};
